import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const KEY = "intro_seen_v1";

/**
 * Full-screen intro that plays once per browser session.
 * - Coin flies in with a 3D spin, glow pulses, logo type appears
 * - Promise line slides up, then the splash fades out
 * - User can tap "Skip" at any time; reduced-motion users auto-skip
 */
export default function BrandIntro() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      sessionStorage.setItem(KEY, "1");
      return;
    }
    if (sessionStorage.getItem(KEY)) return;
    setVisible(true);
    // Preload the hero coin so it looks instant inside the intro
    const img = new Image();
    img.src = "/brand/logo.png";
    const t = setTimeout(() => dismiss(), 4200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(KEY, "1");
    setVisible(false);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          key="brand-intro"
          data-testid="brand-intro"
          role="dialog"
          aria-label="Welcome to RupeeTether"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          onClick={dismiss}
          className="fixed inset-0 z-[100] flex cursor-pointer flex-col items-center justify-center overflow-hidden bg-slate-950"
        >
          {/* Ambient aurora */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="pointer-events-none absolute inset-0"
          >
            <div
              className="absolute left-1/2 top-1/2 h-[90vmin] w-[90vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, hsl(44 86% 60% / 0.42) 0%, transparent 60%)",
              }}
            />
            <div
              className="absolute left-1/4 top-1/3 h-80 w-80 rounded-full opacity-70"
              style={{
                background:
                  "radial-gradient(circle, hsl(186 72% 48% / 0.22) 0%, transparent 65%)",
              }}
            />
            <div
              className="absolute right-10 bottom-1/4 h-80 w-80 rounded-full opacity-70"
              style={{
                background:
                  "radial-gradient(circle, hsl(145 72% 48% / 0.18) 0%, transparent 65%)",
              }}
            />
          </motion.div>

          {/* Skip button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            className="absolute right-4 top-4 rounded-full border border-amber-400/30 bg-white/5 px-3 py-1.5 text-xs font-medium text-amber-100 backdrop-blur transition-colors hover:bg-white/10"
            data-testid="brand-intro-skip"
          >
            Skip intro
          </button>

          {/* 3D coin */}
          <div
            className="relative mb-6"
            style={{ perspective: 1400 }}
            data-testid="brand-intro-coin-wrap"
          >
            <motion.div
              initial={{ scale: 0.1, rotateY: -540, opacity: 0, y: -40 }}
              animate={{ scale: 1, rotateY: 0, opacity: 1, y: 0 }}
              transition={{ duration: 1.6, ease: [0.22, 0.9, 0.3, 1] }}
              style={{ transformStyle: "preserve-3d" }}
              className="relative"
            >
              <motion.img
                src="/brand/logo.png"
                alt=""
                className="h-56 w-56 rounded-full sm:h-72 sm:w-72"
                style={{ filter: "drop-shadow(0 30px 60px hsl(44 86% 30% / 0.6))" }}
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
              />
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    "linear-gradient(100deg, transparent 20%, hsl(44 100% 80% / 0.6) 50%, transparent 80%)",
                  mixBlendMode: "overlay",
                }}
                initial={{ x: "-120%" }}
                animate={{ x: "120%" }}
                transition={{ delay: 1.4, duration: 1.3, ease: "easeInOut" }}
              />
            </motion.div>
          </div>

          {/* Wordmark */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="text-center"
          >
            <p className="font-display text-5xl font-semibold tracking-tight sm:text-6xl">
              Rupee
              <span className="bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">
                {" "}↔{" "}
              </span>
              <span className="bg-gradient-to-br from-emerald-200 via-emerald-400 to-emerald-600 bg-clip-text text-transparent">
                Tether
              </span>
            </p>
            <p className="mt-2 text-sm uppercase tracking-[0.3em] text-amber-200/80">
              INR ↔ USDT exchange
            </p>
          </motion.div>

          {/* Promise line slides up last */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.1, duration: 0.6 }}
            className="mt-10 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-white/5 px-4 py-2 text-xs font-medium text-amber-100 backdrop-blur sm:text-sm"
            data-testid="brand-intro-promise"
          >
            <ShieldCheck className="h-4 w-4 text-amber-300" />
            <span className="num font-semibold text-white">Every payment verified within 15 minutes</span>
          </motion.div>

          {/* Tap-to-continue hint */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.6, duration: 0.4 }}
            className="absolute bottom-6 text-[11px] uppercase tracking-widest text-amber-200/60"
          >
            Tap anywhere to continue
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
